import axios from "axios";
import { AppState, AppThunk } from "@Store/index";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import config from "@Config/index";
import { getMetaSuccess } from "@App/appReducer";
import { CROSS_PAGE_META } from "../constants/amberPlusForexConstants";

interface AmberPlusCrossPageData {
  readyStatus: "invalid" | "request" | "success" | "failure";
  error: string | null;
  data: {
    partners: any[];
    services: any[];
  };
}

export const initialState: AmberPlusCrossPageData = {
  readyStatus: "invalid",
  error: null,
  data: {
    partners: [],
    services: [],
  },
};

const amberPlusCrossPageData = createSlice({
  name: "amberPlusCrossPageData",
  initialState,
  reducers: {
    getCrossPageRequesting: (state: AmberPlusCrossPageData) => {
      state.readyStatus = "request";
    },
    getCrossPageSuccess: (state: AmberPlusCrossPageData, { payload }: PayloadAction<any>) => {
      state.readyStatus = "success";
      state.data.partners = payload.data.partners;
      state.data.services = payload.data.services;
    },
    getCrossPageFailure: (state: AmberPlusCrossPageData, { payload }: PayloadAction<any>) => {
      state.readyStatus = "failure";
      state.error = JSON.stringify(payload);
    },
  },
});

const shouldFetchPage = (state: AppState) =>
  (state as any).amberPlusCrossPageData?.readyStatus === "invalid";

const getCmsUrl = (path: string) =>
  __CLIENT__
    ? `${window.location.origin}/proxy/${config.CMS_API_URL}${path}`
    : `${config.CMS_API_URL}${path}`;

const fetchCrossPageData = (): AppThunk => async (dispatch) => {
  dispatch(getCrossPageRequesting());
  try {
    // Same CMS endpoint as the AmberPlus homepage; we only consume `partners` here.
    // Services list stays empty until a real service-tab API is wired up.
    const res = await axios.get(getCmsUrl("/api/amberplus-page/homepage"));
    const partners = (res.data?.partners || []).map((p: any) => ({
      id: String(p.id),
      fieldData: {
        name: p.name ?? "",
        "partner-image": p.logo
          ? { fileId: "", url: p.logo.url ?? "", alt: p.logo.alt ?? null }
          : null,
      },
    }));
    dispatch(
      getCrossPageSuccess({
        data: {
          partners,
          services: [],
        },
      }),
    );
  } catch (err: any) {
    dispatch(getCrossPageFailure(err));
  }
};

export const fetchAmberPlusCrossPageDataIfNeeded = (): AppThunk => (dispatch, getState: any) => {
  if (!shouldFetchPage(getState())) return null;
  return dispatch(fetchCrossPageData());
};

export const setAmberPlusCrossMeta =
  (slug?: string, url?: string | null): AppThunk =>
  (dispatch) => {
    const isKnownService = Boolean(slug && CROSS_PAGE_META[slug]);
    const pageMeta = isKnownService ? CROSS_PAGE_META[slug as string] : CROSS_PAGE_META.overview;
    const canonicalPath = isKnownService ? `/plus-cross/${slug}` : "/plus-cross";
    dispatch(
      getMetaSuccess({
        ...pageMeta,
        url: url || canonicalPath,
        canonical_url_suffix: canonicalPath,
        index: true,
      } as any),
    );
  };

export default amberPlusCrossPageData.reducer;
export const { getCrossPageRequesting, getCrossPageSuccess, getCrossPageFailure } =
  amberPlusCrossPageData.actions;
